import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { roasterSchema, type Roaster, type RoasterFormData } from '../../types/roaster';
import { useLanguage } from '../../features/language/useLanguage';

interface RoasterFormProps {
  initialData?: Partial<Roaster>;
  onSubmit?: (data: RoasterFormData) => Promise<void>;
  onCancel: () => void;
  readOnly?: boolean;
}

export const RoasterForm: React.FC<RoasterFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RoasterFormData>({
    resolver: zodResolver(roasterSchema),
    defaultValues: {
      name: initialData?.name || '',
      isActive: initialData?.isActive ?? true,
      notes: initialData?.notes || '',
    },
  });

  const handleFormSubmit = async (data: RoasterFormData) => {
    if (onSubmit) {
      await onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.customer.name')}</label>
          <input
            data-test-id="roaster-form-name"
            {...register('name')}
            disabled={readOnly}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
          />
          {errors.name && <p className="text-red-600 text-sm">{errors.name.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.common.status')}</label>
          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <select
                data-test-id="roaster-form-status"
                value={field.value ? 'active' : 'inactive'}
                onChange={(event) => field.onChange(event.target.value === 'active')}
                disabled={readOnly}
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="active">{t('forms.common.statusActive')}</option>
                <option value="inactive">{t('forms.common.statusInactive')}</option>
              </select>
            )}
          />
        </div>
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            {t('forms.common.internalNotes')}
          </label>
          <textarea
            id="notes"
            data-test-id="roaster-form-notes"
            {...register('notes')}
            disabled={readOnly}
            rows={4}
            placeholder={t('production.roasterForm.notesPlaceholder')}
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500"
          />
          {errors.notes && <p className="text-red-600 text-sm">{errors.notes.message}</p>}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-test-id="roaster-form-cancel"
          onClick={onCancel}
          className="px-4 py-2 border rounded-md"
        >
          {t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <button
            type="submit"
            data-test-id="roaster-form-submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {initialData ? t('inventory.modal.actions.saveChanges') : t('production.roasterForm.createRoaster')}
          </button>
        )}
      </div>
    </form>
  );
};
